import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import ActionBar from '../../layout/ActionBar';
import AnnouncementForm from './components/AnnouncementForm';
import Delete from './slideout/Delete';
import Loading from '../../common/Loading';
import SlidingPane from 'react-sliding-pane';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../../util/pageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import { getAnnouncementById, saveAnnouncement } from '../../../api/announcementApi';

interface AnnouncementViewModel {
  message?: string;
  announcementDate?: string;
  expiryDate?: string;
  [key: string]: unknown;
}

function Announcement() {
  const [announcement, setAnnouncement] = useState<AnnouncementViewModel | null>(null);
  const [pageTitle, setPageTitle] = usePageTitle(['Announcements', 'Settings']);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm();

  const onSubmit = (data: AnnouncementViewModel) => {
    const payload = (announcement ? { ...announcement, ...data } : data) as AnnouncementViewModel;
    saveAnnouncement(payload as any)
      .then(() => {
        setAnnouncement(payload);
        toast.success(t('Announcement saved'));
      })
      .catch((ex: { message?: string }) => {
        toast.error(ex.message ?? t('Failed to save announcement'));
      });
  };

  const [deleteSlideoutOpen, setDeleteSlideoutOpen] = useState(false);
  const handleDelete = (e: React.MouseEvent) => {
    (e as any).cancelBubble = true;
    setDeleteSlideoutOpen(true);
    return false;
  };

  useEffect(() => {
    if (id) {
      getAnnouncementById(id)
        .then((d: any) => {
          setAnnouncement(d);
          setPageTitle([d.message ?? '', 'Announcements', 'Settings']);
        })
        .catch((e) => {
          console.error(e);
          navigate('/settings/announcements/');
          toast.error(t('Announcement not found'));
        });
    }
  }, [id, navigate, setPageTitle, t]);

  return announcement ? (
    <>
      <ActionBar
        breadcrumb={[
          { link: '/settings/announcements', label: t('Announcements') },
          { label: announcement.message },
        ]}
      >
        <input
          type="button"
          value={t('Delete')}
          className="btn btn-danger me-2"
          onClick={handleDelete}
        />
        <input
          type="submit"
          value={t('Save')}
          className="btn btn-primary"
          onClick={handleSubmit(onSubmit)}
        />
      </ActionBar>
      <AnnouncementForm
        announcement={announcement}
        register={register}
        errors={errors}
        control={control}
      ></AnnouncementForm>

      <SlidingPane
        isOpen={deleteSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setDeleteSlideoutOpen(false)}
      >
        <Delete
          id={id}
          done={() => {
            setDeleteSlideoutOpen(false);
            navigate('/settings/announcements/');
          }}
          cancel={() => setDeleteSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  ) : (
    <Loading></Loading>
  );
}

export default Announcement;
